/**
 * Gift card money formatting — currency-aware (gift cards may settle in non-USD),
 * building on the shared cents convention. Amounts are in cents on the wire.
 */
// A cached null marks a code Intl rejected (RangeError on a malformed or unrecognized currency),
// which keeps the warning to once per code per session. Caching a *USD* formatter under the bad
// key instead would be worse than not caching: it relabels the amount, permanently.
const formatters = new Map<string, Intl.NumberFormat | null>();

const formatterFor = (currency: string): Intl.NumberFormat | null => {
  if (!formatters.has(currency)) {
    try {
      formatters.set(currency, new Intl.NumberFormat('en-US', { style: 'currency', currency }));
    } catch (error) {
      console.warn(`Unsupported gift card currency code '${currency}'; showing the raw code instead:`, error);
      formatters.set(currency, null);
    }
  }
  return formatters.get(currency) ?? null;
};

/**
 * The list query returns `number` already masked, as `***` + the last three
 * digits (see the masking step in reware-api's `giftcards` resolver). Callers
 * must not add their own mask on top — swap the server's asterisks for bullets
 * so it reads as redaction rather than as literal characters in the number.
 */
export const formatMaskedNumber = (number: string | null | undefined): string =>
  (number ? number.replace(/^\*+/, '•••') : '•••');

/**
 * Format a cents amount in the gift card's transaction currency (default USD).
 * An unsupported code renders as `<amount> <CODE>` rather than being coerced to
 * USD — a partner reconciling balances must not be shown the wrong currency.
 */
export const formatMoney = (
  cents: number | null | undefined,
  currency: string | null | undefined = 'USD'
): string => {
  if (cents == null) return '—';
  // `|| 'USD'` and not the default parameter: transactionCurrencyCode is
  // nullable, and a default only fires for undefined.
  const code = currency || 'USD';
  const amount = cents / 100;
  return formatterFor(code)?.format(amount) ?? `${amount.toFixed(2)} ${code}`;
};
